import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

import {
  QUICK_ADD_ICON_TYPE,
  QUICK_ADD_HORIZONTAL_ALIGNMENT,
} from '../../../../constants';
import { tpaColorName } from '../../../../../../utils/color';

export type AddToCartButtonStylesParams = {
  quickAddButtonStyle: StyleParamType.Number;
  quickAddButtonContent: StyleParamType.Number;
  quickAddButtonWidth: StyleParamType.Number;
  quickAddIconType: StyleParamType.Number;
  quickAddHorizontalAlignment: StyleParamType.Number;

  quickAddDefaultFont: StyleParamType.Font;
  quickAddDefaultTextColor: StyleParamType.Color;
  quickAddDefaultBackgroundColor: StyleParamType.Color;
  quickAddDefaultBorderWidth: StyleParamType.Number;
  quickAddDefaultBorderColor: StyleParamType.Color;
  quickAddCornerRadiusTopLeft: StyleParamType.Number;
  quickAddCornerRadiusTopRight: StyleParamType.Number;
  quickAddCornerRadiusBottomLeft: StyleParamType.Number;
  quickAddCornerRadiusBottomRight: StyleParamType.Number;
  quickAddCornerRadiusLinked: StyleParamType.Number;

  quickAddHoverFont: StyleParamType.Font;
  quickAddHoverTextColor: StyleParamType.Color;
  quickAddHoverBackgroundColor: StyleParamType.Color;
  quickAddHoverBorderColor: StyleParamType.Color;
};

/**
 *  Alignment's default depends on placement, which a style param's
 * `getDefaultValue` can't read — so it's parameterized here instead.
 */
export const addToCartButtonStylesParamsFactory = (
  defaultAlignment: QUICK_ADD_HORIZONTAL_ALIGNMENT = QUICK_ADD_HORIZONTAL_ALIGNMENT.LEFT,
) =>
  createStylesParams<AddToCartButtonStylesParams>({
    quickAddButtonStyle: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Visual style of the quick add button: 0=Filled, 1=Outlined, 2=Pill Filled, 3=Pill Outlined',
        },
      },
    },
    quickAddButtonContent: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Content shown in the quick add button: 0=Icon only, 1=Text only, 2=Icon and text',
        },
      },
    },
    quickAddButtonWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Width behaviour of the quick add button: 0=Stretched (full width), 1=Fix to text (hug content)',
        },
      },
    },
    quickAddIconType: {
      type: StyleParamType.Number,
      getDefaultValue: () => QUICK_ADD_ICON_TYPE.PLUS,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: QUICK_ADD_ICON_TYPE.PLUS },
        ai: {
          description:
            'Preset icon shown in the quick add button: 0=Cart, 1=EcomCart, 2=Stores, 3=ShoppingBag, 4=Plus',
        },
      },
    },
    quickAddHorizontalAlignment: {
      type: StyleParamType.Number,
      getDefaultValue: () => defaultAlignment,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: {
          type: 'static',
          value: QUICK_ADD_HORIZONTAL_ALIGNMENT.LEFT,
        },
        ai: {
          description:
            'Horizontal alignment of the quick add button: 0=Left, 1=Center, 2=Right',
        },
      },
    },
    quickAddDefaultFont: {
      type: StyleParamType.Font,
      getDefaultValue: (params) =>
        wixFontParam('Body-M', {
          htmlTag: 'p',
          size: 14,
        })(params),
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description: 'Font of the quick add/remove (+/−) button label',
        },
      },
    },
    quickAddDefaultTextColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam(
        tpaColorName('wst-button-color-text-primary'),
        1,
      ),
      metadata: {
        type: StyleType.color,
        defaultValue: {
          type: 'static',
          preset: tpaColorName('wst-button-color-text-primary'),
          opacity: 1,
        },
        ai: { description: 'Text/icon color of the quick add/remove button' },
      },
    },
    quickAddDefaultBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam(
        tpaColorName('wst-button-primary-background-color'),
        1,
      ),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: {
          type: 'static',
          preset: tpaColorName('wst-button-primary-background-color'),
          opacity: 1,
        },
        ai: { description: 'Background color of the quick add/remove button' },
      },
    },
    quickAddDefaultBorderWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Border thickness in pixels of the quick add/remove button',
        },
      },
    },
    quickAddDefaultBorderColor: {
      type: StyleParamType.Color,
      // Matches the FILLED preset (the default quickAddButtonStyle) — fully
      // transparent border. Button-style presets in AddToCartButton/index.tsx
      // overwrite just the opacity when a different preset is picked.
      getDefaultValue: wixColorParam(tpaColorName('wst-color-action'), 0),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: {
          type: 'static',
          preset: tpaColorName('wst-color-action'),
          opacity: 0,
        },
        ai: { description: 'Border color of the quick add/remove button' },
      },
    },
    quickAddCornerRadiusTopLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-left corner radius in pixels of the quick add/remove button',
        },
      },
    },
    quickAddCornerRadiusTopRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-right corner radius in pixels of the quick add/remove button',
        },
      },
    },
    quickAddCornerRadiusBottomLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-left corner radius in pixels of the quick add/remove button',
        },
      },
    },
    quickAddCornerRadiusBottomRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-right corner radius in pixels of the quick add/remove button',
        },
      },
    },
    quickAddCornerRadiusLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            'Controls whether the quick add/remove button corner radii are linked — 1 = all corners locked together, 0 = each corner independent',
        },
      },
    },

    quickAddHoverFont: {
      type: StyleParamType.Font,
      getDefaultValue: (params) =>
        wixFontParam('Body-M', {
          htmlTag: 'p',
          size: 14,
        })(params),
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description: 'Font of the quick add/remove button label on hover',
        },
      },
    },
    quickAddHoverTextColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam(
        tpaColorName('wst-button-color-text-primary'),
        1,
      ),
      metadata: {
        type: StyleType.color,
        defaultValue: {
          type: 'static',
          preset: tpaColorName('wst-button-color-text-primary'),
          opacity: 1,
        },
        ai: {
          description:
            'Text/icon color of the quick add/remove button on hover',
        },
      },
    },
    quickAddHoverBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam(
        tpaColorName('wst-button-primary-background-color'),
        1,
      ),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: {
          type: 'static',
          preset: tpaColorName('wst-button-primary-background-color'),
          opacity: 1,
        },
        ai: {
          description:
            'Background color of the quick add/remove button on hover',
        },
      },
    },
    quickAddHoverBorderColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam(
        tpaColorName('wst-button-primary-background-color'),
        0.12,
      ),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: {
          type: 'static',
          preset: tpaColorName('wst-button-primary-background-color'),
          opacity: 0.12,
        },
        ai: {
          description: 'Border color of the quick add/remove button on hover',
        },
      },
    },
  });

export const addToCartButtonStylesParams = addToCartButtonStylesParamsFactory();
